import isNil from 'lodash/isNil';
import {
  Dayjs,
} from 'utils/dayjs';
import {
  formatTime,
} from 'utils/date';
import {
  notEmptyString,
} from '@populateui/utils';

export type DurationOption = {
  minutes: number;
  label: string;
};

export const DEFAULT_DURATION_LIST = [15, 30, 45, 60, 90, 120, 180, 240, 360, 480];

export function formatDurationLabel(minutes: number): string {
  return `${minutes} min`;
}

export function parseDurationInput(raw: string): number | null {
  const trimmed = raw.trim().replace(/\s*(min|mins|minutes)\s*$/i, '');
  const match = trimmed.match(/^(\d+)$/);
  const minutes = match ? Number(match[1]) : null;

  return !isNil(minutes) && minutes > 0 ? minutes : null;
}

export function toDurationOptions(durationList: number[]): DurationOption[] {
  return durationList.map((minutes) => ({
    minutes,
    label: formatDurationLabel(minutes),
  }));
}

export function toTypedDurationOption(inputValue: string): DurationOption | null {
  const parsed = parseDurationInput(inputValue);

  return isNil(parsed)
    ? null
    : {
      minutes: parsed,
      label: formatDurationLabel(parsed),
    };
}

export function buildDurationOptions(
  durationList: number[],
  typedOption: DurationOption | null,
): DurationOption[] {
  const baseOptions = toDurationOptions(durationList);

  return isNil(typedOption)
    || baseOptions.some((option) => option.minutes === typedOption.minutes)
    ? baseOptions
    : [typedOption, ...baseOptions];
}

export function getEndTime(
  startTime: Dayjs | null,
  durationMinutes: number | null,
): Dayjs | null {
  return (
    !isNil(startTime)
    && startTime.isValid()
    && !isNil(durationMinutes)
    && durationMinutes > 0
  )
    ? startTime.add(durationMinutes, 'minute')
    : null;
}

export function formatEndTimeLabel(endTime: Dayjs | null): string {
  return endTime?.isValid() ? formatTime(endTime) : '';
}

export function toInputValue(value: number | null): string {
  return isNil(value) ? '' : formatDurationLabel(value);
}

export function normalizeDurationOnBlur(
  raw: string,
  lastValidMinutes: number,
): number {
  const parsed = parseDurationInput(raw);

  return notEmptyString(raw) && !isNil(parsed)
    ? parsed
    : lastValidMinutes;
}
